import styled, {css} from 'styled-components';
import {media} from '../../styles/components/media-queries';
import {Link1} from '../../styles/components/links';
import {Button3a} from '../../styles/components/buttons';
import {ArrowSwitch} from '../../styles/components/arrowSwitch';

export const ToolBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  padding: 0 2rem;
  background-color: ${p => p.theme.secondaryBgColor};
  ${media.smaller`
    padding: 0.8rem 2rem;
    justify-content: space-between;
  `};
`;

export const ProductCount = styled.span`
  font-size: 1.2rem;
  font-weight: bold;
  white-space: nowrap;
  color: ${p => p.theme.textColor};
  ${media.smaller`
    font-size: 1rem;
  `};
`;

export const SortWrapper = styled.div`
  position: relative;
`;

export const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  height: 3.2rem;
  min-height: 3.2rem;
  padding: 0 1rem 0 1.2rem;
  border: solid 0.2rem ${p => p.theme.secondaryColor};
  background: transparent;
  color: ${p => p.theme.secondaryColor};
  font-size: 1.4rem;
  white-space: nowrap;
  cursor: pointer;

  &::after {
    font-family: 'icomoon';
    content: '\\e910';
    font-size: 2rem;
    line-height: 1;
  }
`;

export const SortDropdownPanel = styled.ul`
  position: absolute;
  top: calc(100% + 0.4rem);
  left: 0;
  min-width: 100%;
  background: #fff;
  border: 1px solid ${p => p.theme.borderColor};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 200;
  list-style: none;
  margin: 0;
  padding: 0.4rem 0;
`;

export const SortDropdownItem = styled.li`
  padding: 0.8rem 1.6rem;
  font-size: 1.4rem;
  color: ${p => (p.$active ? p.theme.secondaryColor : p.theme.textColor)};
  font-weight: ${p => (p.$active ? 'bold' : 'normal')};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${p => p.theme.secondaryBgColor};
  }
`;

export const Container = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.secondaryBgColor};
  padding: 1rem;
  ${media.medium`
    margin-top: 0;
  `};
  ${media.smaller`
    padding: 0;
  `};
`;

export const BreadCrumb = styled.nav`
  width: 100%;
  min-height: 4rem;
  background-color: ${props => props.theme.secondaryBgColor};
  font-size: 1.2rem;
  padding: 2rem;
  color: ${props => props.theme.primaryColor};
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${media.smaller`
    margin-top: 1rem;
    padding: 1rem 2rem 1rem;
    line-height: 1.6rem;
  `};
`;

export const BreadCrumbLinks = styled(Link1)`
  text-transform: capitalize;
`;

export const CurrentPage = styled.a`
  font-weight: ${p => (p.$normal ? 'normal' : 'bold')};
  text-decoration: none;
  padding-left: ${p => (p.$noPadding ? 0 : '0.5rem')};
  cursor: ${p => (p.$normal ? 'pointer' : 'default')};
  color: ${p => p.theme.primaryColor};
`;

export const Separator = styled.span`
  padding-right: 0.5rem;
  font-weight: normal;
`;

export const FilterOverlayBackdrop = styled.div`
  display: ${p => (p.$show ? 'block' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 999;
  cursor: pointer;
`;

export const FiltersWrapper = styled.aside`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  width: 32rem;
  max-width: 92vw;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #e0e0dc;
  z-index: 1000;
  transform: ${p => (p.$showInMobile ? 'translateX(0)' : 'translateX(-100%)')};
  transition: transform 0.22s ease;
  box-shadow: ${p =>
    p.$showInMobile ? '4px 0 16px rgba(0,0,0,0.15)' : 'none'};
`;

export const FiltersScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 8px 0 0;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 2px;
  }
`;

export const FilterInfo = styled.div`
  padding: 4px 16px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  max-height: 3rem;
`;

export const ResultsPanel = styled.article`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

// The product card grid, shared by the search results and the related-products strip under them
// so the two always lay out the same way: fixed-width cards on wide screens, one full-width column
// on small ones.
const productGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, 35rem);
  justify-content: center;
  column-gap: 1rem;
  row-gap: 0;

  > article {
    min-width: 0;
    max-width: 100%;
  }

  ${media.smaller`
    grid-template-columns: repeat(auto-fill, 100%);
    row-gap: 1rem;
  `};
`;

export const ResultsWrapper = styled.div`
  padding-top: 0.8rem;
  padding-bottom: 0.8rem;

  .infinite-scroll-component {
    ${productGrid}
    overflow: hidden !important;
    max-width: ${({$maxColumns}) =>
      $maxColumns
        ? `calc(${$maxColumns} * 35rem + ${$maxColumns - 1} * 1rem)`
        : '100%'};
    margin: 0 auto;
  }

  ${media.smallest`
    padding-right: 0;
  `};
`;

export const ResultsTitle = styled.div`
  color: ${props => props.theme.secondaryColor};
  background-color: ${props => props.theme.secondaryBgColor};
  font-size: 3.6rem;
  font-weight: lighter;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 4rem;
  margin: 0;
  ${media.medium`
    font-size: 2.4rem;
  `};
  ${media.small`
    font-size: 2rem;
  `};
`;

export const LoadMore = styled.div`
  padding: 2rem;
  width: 100%;
  text-align: center;
`;

export const Title = styled.h3`
  font-size: 1.4rem;
  font-weight: normal;
  color: ${p => p.theme.textColor};
`;

export const FilterPillsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 34.5rem);
  justify-content: center;
  column-gap: 1rem;
  padding: 0.8rem 0;

  > div {
    grid-column: 1 / -1;
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
  }

  ${media.smaller`
    display: flex;
    justify-content: flex-start;
    padding: 0.8rem 2rem;
  `};
`;

export const FilterPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: #fff;
  border: 1px solid ${p => p.theme.primaryColor};
  color: ${p => p.theme.primaryColor};
  border-radius: 2rem;
  padding: 0.3rem 1rem;
  font-size: 1.2rem;
  cursor: pointer;
  white-space: nowrap;

  &::after {
    content: '×';
    font-size: 1.4rem;
    line-height: 1;
    margin-left: 0.2rem;
  }

  &:hover {
    background: ${p => p.theme.primaryColor};
    color: #fff;
  }
`;

export const ClearFilter = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  color: ${props => props.theme.primaryColor};
  text-decoration: none;
  cursor: pointer;
`;

export const FilterButtonWrapper = styled.div`
  flex-shrink: 0;
`;

export const FilterButton = styled(Button3a)`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  width: fit-content;
  height: 3.2rem;
  min-height: 3.2rem;
  padding: 0 1.2rem;
  font-weight: normal;
  text-transform: capitalize;
  white-space: nowrap;
  ${media.smaller`
    padding: 4px 12px;
  `};
`;

export const Status = styled(ArrowSwitch)`
  display: none;
  ${media.smaller`
    display: block;
    &::before {
      font-size: 2.2rem;
    }
  `};
`;

export const SearchFooter = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: 2rem 1rem;
`;

// Starts on the page edge, in line with the breadcrumb and toolbar (2rem): Container already adds
// 1rem on larger screens and nothing on small ones.
export const NoResult = styled.div`
  font-size: 1.4rem;
  color: ${props => props.theme.textColor};
  padding: 2rem 1rem;
  ${media.smaller`
    padding: 2rem;
  `};
`;
export const FiltersButtons = styled.div`
  display: flex;
  justify-content: space-evenly;
  padding: 0.6rem 1rem 1rem;
`;

export const ClearFilterButton = styled(Button3a)`
  text-transform: uppercase;
  width: 50%;
  margin-left: 0.5rem;
  color: ${props => props.theme.secondaryColor};
  border-color: ${props => props.theme.secondaryColor};

  &:hover {
    color: ${props => props.theme.primaryTextColor};
  }
`;

export const DoneButton = styled(ClearFilterButton)`
  margin-right: 0.5rem;
  margin-left: 0;
  width: 100%;
  background-color: ${props => props.theme.secondaryColor};
  color: ${props => props.theme.primaryTextColor};
`;

export const LoadMoreButton = styled(Button3a)`
  text-transform: uppercase;
  width: 50%;
  margin-top: 2rem;
  max-width: 23rem;
  ${media.small`
    width: 18rem;
    min-height: 3rem;
    height: 3rem;
  `};
  ${media.smaller`
    display: none;
  `};
`;

export const NameWrapper = styled.h1`
  font-size: 3.3rem;
  font-weight: normal;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 50rem;
  ${media.medium`
    max-width: 40rem;
    text-align: center;
  `};
  ${media.small`
    font-size: 2.2rem;
    max-width: 32rem;
    text-align: center;
  `};
  ${media.smaller`
    font-size: 2rem;
  `};
`;

export const BreadCrumbResult = styled.span`
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70rem;
  vertical-align: top;
  text-transform: capitalize;
  ${media.medium`
    max-width: 50rem;
  `};
  ${media.small`
    max-width: 40rem;
  `};
  ${media.smaller`
    max-width: 26rem;
  `};
  ${media.smallest`
    max-width: 22rem;
  `};
`;

export const CategoryDescription = styled.h3`
  font-weight: normal;
  color: ${props => props.theme.textColor};
  background-color: ${props => props.theme.tertiaryBgColor};
  padding: 1.2rem 2rem;
  font-size: 1.6rem;
  line-height: 2.4rem;
  margin: 1.7rem 0.7rem 0 0.7rem;
`;

// ── PANDG CAMPAIGN BANNER + PILL STRIP ──────────────────────────────
// Only rendered on SearchPage when arriving via a /pandg link (see
// PandgPage), never part of the normal search results layout.
export const PandgBannerSection = styled.div`
  width: 100%;
  background-color: ${p => p.theme.secondaryBgColor};
  padding: 0 2rem 2rem 2rem;
  ${media.small`
    padding: 0 0 1rem 0;
  `};
`;

export const PandgBanner = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 1rem;
  min-height: 20rem;
  display: flex;
  align-items: center;
  ${media.small`
    border-radius: 0;
    margin: 0;
    min-height: 22rem;
  `};
`;

export const PandgBannerImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 38%;
`;

export const PandgBannerOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(8, 9, 74, 0.93) 0%,
    rgba(8, 9, 74, 0.65) 52%,
    rgba(8, 9, 74, 0.08) 100%
  );
`;

export const PandgBannerContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 2rem 2rem 1rem;
  max-width: 60rem;

  ${media.small`
    padding: 0 1.8rem;
    max-width: 100%;
  `};
`;

export const PandgLogoBadge = styled.div`
  display: block;
  flex-shrink: 0;
  border-radius: 1rem;
`;

export const PandgLogo = styled.img`
  display: block;
  height: 7rem;
  width: auto;
`;

export const PandgEyebrow = styled.span`
  display: inline-block;
  background: rgba(70, 152, 204, 0.22);
  border: 1px solid #4698cc;
  color: #bfe3ff;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.12rem;
  padding: 0.4rem 1.1rem;
  border-radius: 2rem;
  width: fit-content;
`;

export const PandgHeading = styled.h2`
  color: ${p => p.theme.primaryTextColor};
  font-size: 2.4rem;
  font-weight: 800;
  line-height: 1.18;
  margin-bottom: 0.8rem;

  ${media.small`
    font-size: 2.1rem;
  `};
`;

export const PandgSub = styled.p`
  color: rgba(255, 255, 255, 0.82);
  font-size: 1.35rem;
  line-height: 1.55;
  max-width: 48rem;

  strong {
    color: ${p => p.theme.primaryTextColor};
    font-weight: 700;
  }
`;

export const PandgPillSection = styled.div`
  width: 100%;
  background-color: ${p => p.theme.secondaryBgColor};
  padding: 0 2rem 0.5rem 2rem;
  ${media.small`
    padding: 0 1rem;
  `};
`;

export const PandgPillStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  padding-bottom: 1.4rem;

  ${media.small`
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: thin;
    padding-bottom: 0.6rem;
  `};
`;

export const PandgChip = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  background: ${p => (p.$active ? '#08094a' : '#fff')};
  border: 1px solid ${p => (p.$active ? '#08094a' : '#d8d8d4')};
  border-radius: 2rem;
  padding: 0.8rem 1.6rem;
  font-size: 1.3rem;
  font-weight: 600;
  color: ${p => (p.$active ? p.theme.primaryTextColor : '#333')};
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    border-color: #4698cc;
  }
`;

export const PandgChipDot = styled.span`
  width: 0.8rem;
  height: 0.8rem;
  border-radius: 50%;
  background: ${p => (p.$active ? '#fff' : '#4698cc')};
  flex-shrink: 0;
`;

// Same layout as the Pandg banner/pill strip above (shown when ?halal=true
// instead of ?pandg=true) — mirrors its structure, recoloured to the /halal
// landing page's green palette instead of P&G's blue.
export const HalalBannerSection = styled.div`
  width: 100%;
  background-color: ${p => p.theme.secondaryBgColor};
  padding: 0 2rem 2rem 2rem;
  ${media.small`
    padding: 0 0 1rem 0;
  `};
`;

export const HalalBanner = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 1rem;
  min-height: 20rem;
  display: flex;
  align-items: center;
  ${media.small`
    border-radius: 0;
    margin: 0;
    min-height: 22rem;
  `};
`;

export const HalalBannerImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 55%;
  ${media.medium`
    object-position: center 48%;
  `};
`;

export const HalalBannerOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(10, 26, 16, 0.86) 0%,
    rgba(10, 26, 16, 0.62) 42%,
    rgba(10, 26, 16, 0.18) 75%,
    rgba(10, 26, 16, 0.05) 100%
  );
`;

export const HalalBannerContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 2rem 2rem 1rem;
  max-width: 60rem;

  ${media.small`
    padding: 0 1.8rem;
    max-width: 100%;
  `};
`;

export const HalalCertBadge = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  line-height: 1;
  margin-bottom: 1rem;
  color: ${p => p.theme.primaryTextColor};

  span:first-child {
    font-size: 3rem;
    font-weight: 700;
  }

  span:last-child {
    font-size: 1rem;
    font-weight: 800;
    letter-spacing: 0.16rem;
    margin-top: 0.2rem;
  }
`;

export const HalalHeading = styled.h2`
  color: ${p => p.theme.primaryTextColor};
  font-size: 2.4rem;
  font-weight: 800;
  line-height: 1.18;
  margin-bottom: 0.8rem;

  ${media.small`
    font-size: 2.1rem;
  `};
`;

export const HalalSub = styled.p`
  color: rgba(255, 255, 255, 0.88);
  font-size: 1.35rem;
  line-height: 1.55;
  max-width: 48rem;
`;

export const HalalPillSection = styled.div`
  width: 100%;
  background-color: ${p => p.theme.secondaryBgColor};
  padding: 0 2rem 0.5rem 2rem;
  ${media.small`
    padding: 0 1rem;
  `};
`;

export const HalalPillStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  padding-bottom: 1.4rem;

  ${media.small`
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: thin;
    padding-bottom: 0.6rem;
  `};
`;

export const HalalChip = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  background: ${p => (p.$active ? '#144a2b' : '#fff')};
  border: 1px solid ${p => (p.$active ? '#144a2b' : '#d8d8d4')};
  border-radius: 2rem;
  padding: 0.8rem 1.6rem;
  font-size: 1.3rem;
  font-weight: 600;
  color: ${p => (p.$active ? p.theme.primaryTextColor : '#333')};
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    border-color: #1c6b3a;
  }
`;

export const HalalChipDot = styled.span`
  width: 0.8rem;
  height: 0.8rem;
  border-radius: 50%;
  background: ${p => (p.$active ? '#fff' : '#1c6b3a')};
  flex-shrink: 0;
`;

// ── "DID YOU MEAN" + RELATED PRODUCTS ───────────────────────────────
// The banner sits above a search answered with a spelling correction; the strip sits under a
// search that found little or nothing (graph-product-search relatedProducts).
export const CorrectionBanner = styled.div`
  font-size: 1.6rem;
  line-height: 2.4rem;
  color: ${props => props.theme.textColor};
  padding: 1.2rem 2rem 0;
`;

export const CorrectedQuery = styled.strong`
  font-weight: bold;
`;

export const SearchInsteadButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-style: italic;
  color: ${props => props.theme.primaryColor};
  text-decoration: underline;
  cursor: pointer;
`;

export const RelatedSection = styled.section`
  padding: 0.8rem 0;
`;

// The page title's own treatment (ResultsTitle + LeftLine/RightLine), as an h2: the page's h1 is
// the search heading above.
// The page title's row, with room between it and the cards under it.
export const RelatedHeadingRow = styled(ResultsTitle)`
  margin-bottom: 2.4rem;
`;

export const RelatedHeading = styled.h2`
  font-size: inherit;
  font-weight: inherit;
  margin: 0;
  text-align: center;
`;

export const NoResultQuery = styled.strong`
  font-weight: bold;
`;

// "Similar to ...": a plain subheading under real results. Spans the whole grid, so it starts at the
// first card's left edge however many columns fit.
export const RelatedSubheading = styled.h2`
  grid-column: 1 / -1;
  margin: 1.6rem 0 1.2rem;
  font-size: 2.2rem;
  font-weight: 600;
  line-height: 1.3;
  color: ${props => props.theme.secondaryColorHover};
  ${media.smaller`
    font-size: 1.8rem;
    margin: 1.2rem 1rem 0.4rem;
  `};
`;

// The results grid's own rules (productGrid), so the strip lays out exactly like the results above
// it at every width.
export const RelatedGrid = styled.div`
  ${productGrid}
`;
